import { StatusLED } from "@/components/infra";
import { LogoMark } from "@/components/hero/HeroShared";
import { BHeroCopy, B_GRID_BG } from "@/components/hero/BHeroCopy";
import { statsFrom, timeLabel } from "@/components/hero/LiveIndex";
import { useHeroLiveStats } from "@/lib/useHeroLiveStats";

const NODES = { roles: { x: 17, y: 15 }, employers: { x: 83, y: 15 }, early: { x: 17, y: 85 }, reach: { x: 83, y: 85 } };

const StatNode = ({ stat, testId, style, className = "" }) => (
  <div data-testid={testId} style={style} className={`chrome-surface px-4 py-3 text-left ${className}`}>
    <span className="block font-display text-xl xl:text-2xl font-bold text-[#0F1117] whitespace-nowrap">{stat.value}</span>
    <span className="block mt-1 font-mono-label text-[10px] !tracking-normal text-[#3D4049]">{stat.label}</span>
    <span className="block text-[10px] text-[#71747B]">{stat.note}</span>
  </div>
);

const Core = ({ className = "" }) => (
  <div className={`hero-a-stage relative ${className}`}>
    <span aria-hidden="true" className="hero-a-bloom" />
    <span aria-hidden="true" className="hero-a-ring" />
    <span aria-hidden="true" className="hero-a-ring hero-a-orbit" />
    <LogoMark testId="hero-option-b2-mark" className="relative w-full dcyb-mark-shadow" />
  </div>
);

const OrbitStage = ({ stats }) => (
  <div data-testid="hero-option-b2-orbit" className="relative hidden sm:block w-full max-w-[560px] mx-auto aspect-square">
    <svg aria-hidden="true" viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full overflow-visible">
      {Object.entries(NODES).map(([id, { x, y }]) => (
        <g key={id}>
          <line x1="50" y1="50" x2={x} y2={y} stroke="rgba(15,17,23,0.22)" strokeWidth="0.25" />
          <line x1="50" y1="50" x2={x} y2={y} stroke="#10B981" strokeWidth="0.45" strokeDasharray="1.5 6" className="hero-b2-flow" />
          <circle cx={x} cy={y} r="1" fill="#10B981" />
        </g>
      ))}
    </svg>
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[30%]"><Core /></div>
    {stats.map((stat) => (
      <StatNode key={stat.id} stat={stat} testId={`hero-option-b2-stat-${stat.id}`} className="absolute w-[34%] -translate-x-1/2 -translate-y-1/2" style={{ left: `${NODES[stat.id].x}%`, top: `${NODES[stat.id].y}%` }} />
    ))}
  </div>
);

export const HeroOptionB2 = () => {
  const live = useHeroLiveStats();
  const stats = statsFrom(live.jobs);
  const updated = timeLabel(live.updatedAt);
  return (
    <section data-testid="hero-option-b2" className="relative overflow-hidden border-b hairline bg-white">
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none opacity-[0.5]" style={B_GRID_BG} />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-12 sm:py-16 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-x-10 gap-y-12 items-center">
        <BHeroCopy prefix="hero-option-b2" eyebrow="Live from the DCYB network" headlineSize="lg:text-[2.6rem] xl:text-[3.05rem]" className="lg:col-span-6" />
        <div className="lg:col-span-6 min-w-0 fade-up delay-200">
          <OrbitStage stats={stats} />
          <div className="sm:hidden">
            <Core className="w-[150px] mx-auto my-6" />
            <div className="relative z-10 mt-10 grid grid-cols-2 gap-2">{stats.map((stat) => <StatNode key={stat.id} stat={stat} testId={`hero-option-b2-stat-m-${stat.id}`} />)}</div>
          </div>
          <p data-testid="hero-option-b2-updated" className="relative z-10 mt-4 flex items-center justify-center gap-2 font-mono-label text-[10px] !tracking-normal text-[#5F6168]"><StatusLED color="#10B981" />{live.loading ? "Syncing live data…" : `Live · ${updated ? `Updated ${updated}` : "Snapshot"} · DCYB job feed + GlobeNewswire 2026`}</p>
        </div>
      </div>
    </section>
  );
};
